// Transaction domain types — Row (DB) + App (with relations + computed pricing).

import type { InvoiceLinkWithInvoice } from './invoice.ts';
import type { PriceType, VatRateCode, TransactionStatus, InvoicedStatus, GDStatus, BorderType, TransportType, Pricing, DocumentType, DocumentStatus, ReviewStatus } from "@shared/types.ts";
import type { CompanyJoin } from "./company.ts";
import type { EmployeeJoin } from "./employee.ts";
import type { ProductJoin } from "./product.ts";


// ── Transaction (DB table) ──────────────────────────────────────────────

export interface TransactionRow {
  id: number;
  reference_number: string;
  external_id: string | null;
  order_number: string | null;

  // Dates and times
  pickup_date_from: string | null;
  pickup_date_to: string | null;
  load_date: string | null;
  load_time: string | null;
  reload_date: string | null;
  unload_date: string | null;
  unload_time: string | null;

  // Measurements
  loaded_weight: number | null;
  unloaded_weight: number | null;
  number_of_bales: number | null;

  // Product
  product_id: number | null;

  // Physical locations
  load_place_company_id: number | null;
  unload_place_company_id: number | null;

  // Commercial parties
  load_commercial_company_id: number | null;
  unload_commercial_company_id: number | null;

  // Customs/legal (cross-border)
  exporter_company_id: number | null;
  importer_company_id: number | null;

  // Load pricing (embedded)
  load_price: number | null;
  load_price_currency: string;
  load_price_type: PriceType | null;
  load_price_vat_rate_code: VatRateCode;
  load_price_incoterms: string | null;
  load_price_list_id: number | null;

  // Unload pricing (embedded)
  unload_price: number | null;
  unload_price_currency: string;
  unload_price_type: PriceType | null;
  unload_price_vat_rate_code: VatRateCode;
  unload_price_incoterms: string | null;
  unload_price_list_id: number | null;

  // Responsible people
  logistics_employee_id: number | null;
  load_sales_employee_id: number | null;
  unload_sales_employee_id: number | null;

  // Status — 'cancelled' marks a transaction as cancelled; null otherwise.
  status_override: TransactionStatus | null;

  // Invoiced status (materialized by trigger)
  load_invoiced_status: InvoicedStatus;
  unload_invoiced_status: InvoicedStatus;
  load_received_invoiced_status: InvoicedStatus;
  unload_received_invoiced_status: InvoicedStatus;

  gd_status: GDStatus | null;
  request_dpr: boolean;
  priority: boolean;

  // Route
  border_type: BorderType;
  transit: string | null;

  // Rule FK
  rule_id: number | null;

  // Recycling
  recycling_process_code: string | null;

  // Audit
  is_deleted: boolean;
  created_at: string;
  updated_at: string;
}
// ── Transaction transport (DB table) ────────────────────────────────────

export interface TransactionTransportRow {
  id: number;
  transaction_id: number;
  sequence_number: number;

  transport_company_id: number | null;
  commercial_company_id: number | null;
  transport_type: TransportType | null;

  driver_name: string | null;
  driver_phone: string | null;
  vehicle_plate: string | null;
  container_number: string | null;

  transit: string | null;

  // Pricing (embedded)
  price: number | null;
  price_currency: string;
  price_type: PriceType | null;
  price_vat_rate_code: VatRateCode;
  price_incoterms: string | null;
  price_list_id: number | null;

  invoice_id: number | null;
  order_file_id: number | null;

  invoiced_status: InvoicedStatus;

  is_deleted: boolean;
  created_at: string;
  updated_at: string;
}
// ── Check types (from transactions_with_checks view) ────────────────────

// The key union derives from the check registry — the single source of truth
// that also generates the DB views (shared/checks/checkRegistry.ts).
import type { CheckKey } from '../checks/checkRegistry.ts';
export type { CheckKey };
/** Tri-state check status from transactions_with_checks view. */
export type CheckStatus = 'completed' | 'pending' | 'not_required';

/** Check fields — one tri-state column per check key. */
/** Check fields — one tri-state column per check key, suffixed with _check. */
export type CheckFields = {
  [K in CheckKey as `${K}_check`]: CheckStatus;
};

/** Manual "handled outside the system" override — value of transactions.check_overrides map. */
export interface CheckOverride {
  at: string;
  by: string | null;
  reason: string;
}
// ── Display types (pre-computed for rendering) ──────────────────────────

export interface Transaction extends TransactionRow, Partial<CheckFields> {
  /** Manual check overrides map (transactions.check_overrides / passed through the view). */
  check_overrides?: Partial<Record<CheckKey, CheckOverride>> | null;

  product?: ProductJoin | null;
  load_place_company?: CompanyJoin | null;
  unload_place_company?: CompanyJoin | null;
  load_commercial_company?: CompanyJoin | null;
  unload_commercial_company?: CompanyJoin | null;
  exporter_company?: CompanyJoin | null;
  importer_company?: CompanyJoin | null;
  logistics_employee?: EmployeeJoin | null;
  load_sales_employee?: EmployeeJoin | null;
  unload_sales_employee?: EmployeeJoin | null;

  /** Computed pricing — set by DAO, absent in raw Refine queries. */
  load_pricing?: Pricing;
  unload_pricing?: Pricing;

  /** Lazy-loaded collections attached by FE after the primary fetch. */
  transports?: TransactionTransportRow[];
  transaction_files?: TransactionFileRow[];
  files?: TransactionFileRow[];
  transaction_folders?: TransactionFolder[];
  comments?: Comment[];
  email_sends?: unknown[];
}

export interface Transport extends TransactionTransportRow {
  company?: CompanyJoin | null;
  commercial_company?: CompanyJoin | null;
  invoiceLinks?: InvoiceLinkWithInvoice[];
  transaction?: Transaction;
  invoice?: InvoiceRow;
  comments?: Comment[];
}

// ── Write-transform metadata ────────────────────────────────────────────
// Fields that exist on the Transaction app type but MUST be stripped before
// writing to Postgres. Consumed by src/providers/appTransforms.ts.

/** Relation names that appear on Transaction but aren't DB columns. */
export const TRANSACTION_RELATIONS: readonly string[] = [
  'product',
  'load_place_company',
  'unload_place_company',
  'load_commercial_company',
  'unload_commercial_company',
  'exporter_company',
  'importer_company',
  'logistics_employee',
  'load_sales_employee',
  'unload_sales_employee',
  'transports',
  'transaction_files',
  'files',
  'transaction_folders',
  'comments',
  'email_sends',
  'load_pricing',
  'unload_pricing',
];

/** Generated / computed DB columns that must never be written. */
export const TRANSACTION_GENERATED: readonly string[] = [
  'search_vector',
];

/** Relations on TransactionTransportRow (write-side) that must be stripped. */
export const TRANSPORT_RELATIONS: readonly string[] = [
  'company',
  'commercial_company',
  'invoiceLinks',
  'transaction',
  'invoice',
  'comments',
];

// ── Transaction rule (checklist rules) ──────────────────────────────────

export interface TransactionRule {
  id: number;
  name: string;
  priority: number;
  // Matching conditions
  load_commercial_company_ids: number[];
  unload_commercial_company_ids: number[];
  unload_place_company_ids: number[];
  load_place_company_ids: number[];
  importer_company_ids: number[];
  exporter_company_ids: number[];
  product_ids: number[];
  border_types: BorderType[];
  has_waste_code: boolean | null;
  // Transport
  transport_complete_required: boolean | null;
  transport_order_generated_required: boolean | null;
  transport_order_sent_required: boolean | null;
  // Notifications
  loading_notification_sent_required: boolean | null;
  unloading_notification_sent_required: boolean | null;
  unload_entry_request_sent_required: boolean | null;
  unloading_confirmation_sent_required: boolean | null;
  // Documents
  annex7_generated_required: boolean | null;
  annex_copy_approved_required: boolean | null;
  annex_original_approved_required: boolean | null;
  cmr_generated_required: boolean | null;
  cmr_copy_approved_required: boolean | null;
  cmr_original_approved_required: boolean | null;
  weight_ticket_copy_approved_required: boolean | null;
  customs_docs_approved_required: boolean | null;
  // Photos & weight
  loading_photo_approved_required: boolean | null;
  unload_weight_set_required: boolean | null;
  number_of_bales_set_required: boolean | null;
  recycling_process_set_required: boolean | null;
  // Pricing & invoices
  unload_price_set_required: boolean | null;
  load_price_set_required: boolean | null;
  income_invoices_issued_required: boolean | null;
  cost_invoices_issued_required: boolean | null;
  load_invoices_issued_required: boolean | null;
  unload_invoices_issued_required: boolean | null;
  transport_invoices_issued_required: boolean | null;
  // Other
  documents_reminder_enabled: boolean | null;
  created_at: string;
  updated_at: string;
}

// ── Document types (drive files/folders) ────────────────────────────────

export interface TransactionFileRow {
  id: number;
  transaction_id: number | null;
  transaction_folder_id: number | null;
  document_type: DocumentType;
  document_status: DocumentStatus;
  drive_file_id: string;
  file_name: string;
  mime_type: string | null;
  is_latest: boolean;
  uploaded_by: string | null;
  created_at: string;
  document_number: string | null;
  received_date: string | null;
  review_status: ReviewStatus;
}

export interface TransactionFile extends TransactionFileRow {
  transaction?: Pick<TransactionRow, 'id' | 'reference_number'> | null;
  /** FK to document_extractions (pipeline scans); null for manual uploads. */
  document_extraction_id?: string | null;
}

export interface TransactionFolder {
  id: number;
  transaction_id: number;
  folder_type: string;
  drive_folder_id: string;
  created_at: string;
}

// ── Refine list view types (flat row with embedded joins) ───────────────

import type { InvoiceRow } from './invoice.ts';
import type { EmailSend, Comment } from './employee.ts';

export interface TransactionWithRelations extends Transaction {
  transports?: Transport[];
  transaction_files?: TransactionFile[];
  files?: TransactionFile[];
  transaction_folders?: TransactionFolder[];
  comments?: Comment[];
  email_sends?: EmailSend[];

  // Derived fields from transactions_with_checks view
  transport_order_na?: boolean;
  annex_na?: boolean;
  docs_review_status?: 'complete' | 'partial' | 'none';

  // From transactions_edpr_view
  waste_code?: string | null;
  recycling_percent?: number | null;
  recycling_process_code_inferred?: string | null;
  existing_edpr_mass_total?: number | null;
  taken_confirmed_mass?: number | null;
  available_edpr_mass?: number | null;

  // From EDPR view joins
  edpr_records?: Array<{ id: number; is_deleted: boolean; confirmed_mass: number | null; edpr_mass: number | null; edpr_documents?: { id: number; internal_number: string | null } }>;
  invoice_links?: Array<{ side: string; invoice_id: number; invoices?: { invoice_number: string } }>;
}
